import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component}
 */

class HeaderComponent extends Component {
  requiredRefs = [];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Whether the header--sticky class is currently applied
   * @type {boolean}
   */
  #isStickyClassActive = false;

  /**
   * Delays removing solid header appearance until the scroll-up hide animation finishes
   * @type {number | null}
   */
  #stickyAppearanceTimeout = null;

  /** @type {number} */
  #stickyAppearanceDelayMs = 300;

  /**
   * Computed `top` offset (px) at scroll 0 — used to collapse header toward top: 0 while scrolling down
   * @type {number}
   */
  #scrollCollapseDistance = 0;

  /** @type {(() => void) | null} Teardown function for desktop nav hover */
  #destroyDesktopNav = null;

  /** @type {(() => void) | null} Teardown function for mobile nav click */
  #destroyMobileNav = null;

  /** @type {(() => void) | null} Teardown function for the account sidebar */
  #destroyAccountSidebar = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);
    this.#measureScrollCollapseDistance();

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #measureScrollCollapseDistance = () => {
    this.style.removeProperty('--header-top-offset');
    const { top } = getComputedStyle(this);
    this.#scrollCollapseDistance = Number.parseFloat(top) || 0;
  };

  #applyStickyAppearance = (solid) => {
    if (solid === this.#isStickyClassActive) return;

    this.#isStickyClassActive = solid;
    this.classList.toggle('header--sticky', solid);
    this.classList.toggle('header--has-scrolled', solid);
  };

  #clearStickyAppearanceTimeout = () => {
    if (this.#stickyAppearanceTimeout === null) return;

    clearTimeout(this.#stickyAppearanceTimeout);
    this.#stickyAppearanceTimeout = null;
  };

  /**
   * Scroll-up: solid colors only while revealed; delay removal until hide transition ends.
   * @param {boolean} revealed
   */
  #setScrollUpAppearance = (revealed) => {
    if (revealed) {
      this.#clearStickyAppearanceTimeout();
      this.#applyStickyAppearance(true);
      return;
    }

    if (!this.#isStickyClassActive) return;

    if (this.#stickyAppearanceTimeout !== null) return;

    this.#stickyAppearanceTimeout = setTimeout(() => {
      this.#stickyAppearanceTimeout = null;

      const stillRevealed =
        this.dataset.stickyState === 'active' ||
        this.classList.contains('header--scroll-up-visible');

      if (!stillRevealed) {
        this.#applyStickyAppearance(false);
      }
    }, this.#stickyAppearanceDelayMs);
  };

  #handleStickyClass = () => {
    if (document.body.style.position === 'fixed') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const stickyMode = this.getAttribute('sticky');

    if (stickyMode === 'scroll-up') {
      const isScrollUpRevealed =
        this.dataset.stickyState === 'active' ||
        this.classList.contains('header--scroll-up-visible');

      this.#setScrollUpAppearance(isScrollUpRevealed);
      return;
    }

    const hasScrolled = scrollTop > 0;

    if (hasScrolled !== this.classList.contains('header--has-scrolled')) {
      this.classList.toggle('header--has-scrolled', hasScrolled);
    }

    const shouldBeSticky = scrollTop > this.offsetHeight;

    if (shouldBeSticky !== this.#isStickyClassActive) {
      this.#isStickyClassActive = shouldBeSticky;
      this.classList.toggle('header--sticky', shouldBeSticky);
    }
  };

  /**
   * Scroll-up sticky: collapse top offset over the first N px of scroll, hide on scroll down, reveal on scroll up.
   * @param {number} scrollTop
   * @param {boolean} isScrollingUp
   */
  #updateScrollUpHeader = (scrollTop, isScrollingUp) => {
    const collapseDistance = this.#scrollCollapseDistance;
    const headerHeight = this.offsetHeight;
    // Total scroll over which the header first collapses the announcement-bar
    // offset (collapseDistance), then slides fully off-screen (its own height).
    const hideDistance = collapseDistance + headerHeight;

    // Near the top: collapse the announcement-bar offset down to 0 and dock.
    if (scrollTop <= collapseDistance) {
      const topOffset = Math.max(0, collapseDistance - scrollTop);

      if (scrollTop === 0) {
        this.style.removeProperty('--header-top-offset');
      } else {
        this.style.setProperty('--header-top-offset', `${topOffset}px`);
      }

      this.#offscreen = false;
      this.classList.remove('header--scroll-up-visible');
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
      return;
    }

    // Mid scroll, still scrolling down and not yet fully hidden: continue the
    // offset into negative values so the header slides up with the scroll,
    // bottoming out at -headerHeight (collapseDistance - scrollTop).
    if (scrollTop < hideDistance && !isScrollingUp) {
      this.style.setProperty('--header-top-offset', `${collapseDistance - scrollTop}px`);

      this.#offscreen = false;
      this.classList.remove('header--scroll-up-visible');
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'down';
      return;
    }

    // Header fully hidden, or scrolling up: hand control back to the scroll-up
    // reveal logic. Remove the inline offset — a fixed -headerHeight value would
    // otherwise pin the header off-screen and block it from sliding into view.
    this.style.removeProperty('--header-top-offset');

    if (isScrollingUp) {
      this.classList.add('header--scroll-up-visible');
      this.dataset.stickyState = 'active';
      this.dataset.scrollDirection = 'up';
    } else {
      this.classList.remove('header--scroll-up-visible');
      this.dataset.stickyState = 'idle';
      this.dataset.scrollDirection = 'down';
    }
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      // Ignore scroll events while the body is locked (dialogs set position:fixed
      // for the iOS-safe scroll lock). The lock resets scrollTop to 0, which would
      // corrupt #lastScrollTop and make the close-time scroll restore look like a
      // downward scroll — collapsing the revealed sticky header.
      if (document.body.style.position === 'fixed') return;
      this.#updateScrollState();
      this.#handleStickyClass();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;

    // A zero-delta scroll event (e.g. the scrollTo() restore when a dialog closes)
    // is not a direction change — preserve the current state instead of treating
    // it as "scrolling down", which would tear down the sticky/revealed classes.
    if (scrollTop === this.#lastScrollTop) return;

    const isScrollingUp = scrollTop < this.#lastScrollTop;

    if (stickyMode === 'scroll-up') {
      this.#updateScrollUpHeader(scrollTop, isScrollingUp);
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (!this.#offscreen && stickyMode !== 'always') return;

    const headerTop = this.getBoundingClientRect().top;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Sets up desktop-only (>=1024px) nav hover behaviour.
   * - mouseenter on a nav item with a sidebar → activate it (show sidebar, lock scroll, show backdrop)
   * - mouseenter on child/grandchild items → reveal their sublist
   * - mouseenter or click on the backdrop → deactivate everything
   * Returns nothing; stores a teardown function in #destroyDesktopNav.
   */
  #initDesktopNav() {
    const DESKTOP = 1024;
    const header = this;
    const bg = this.querySelector('.active__nav-background');
    const navItems = this.querySelectorAll('.header-nav__item');
    const subItems = this.querySelectorAll('.sidebar__item--has-children, .sidebar__subitem--has-children');

    let activeItem = null;
    let navSwitchTimer = null;
    let subNavSwitchTimer = null;

    const startNavSwitch = () => {
      if (navSwitchTimer) clearTimeout(navSwitchTimer);
      header.dataset.ucNavSwitching = '';
      navSwitchTimer = setTimeout(() => {
        delete header.dataset.ucNavSwitching;
        navSwitchTimer = null;
      }, 50);
    };

    const startSubNavSwitch = () => {
      if (subNavSwitchTimer) clearTimeout(subNavSwitchTimer);
      header.dataset.ucSubNavSwitching = '';
      subNavSwitchTimer = setTimeout(() => {
        delete header.dataset.ucSubNavSwitching;
        subNavSwitchTimer = null;
      }, 50);
    };

    const activate = (item) => {
      if (activeItem === item) return;
      if (activeItem) startNavSwitch();
      deactivate();
      activeItem = item;
      item.classList.add('nav__item-active');
      header.dataset.ucNavActive = '';
      document.body.classList.add('lock-scroll');
    };

    const deactivate = () => {
      if (!activeItem) return;
      activeItem.classList.remove('nav__item-active');
      for (const el of activeItem.querySelectorAll('.active')) el.classList.remove('active');
      for (const timer of subLeaveTimers.values()) clearTimeout(timer);
      subLeaveTimers.clear();
      activeItem = null;
      delete header.dataset.ucNavActive;
      document.body.classList.remove('lock-scroll');
    };

    const onNavEnter = (e) => {
      if (window.innerWidth < DESKTOP) return;
      if (e.currentTarget.querySelector('.header__sidebar')) activate(e.currentTarget);
    };

    const subLeaveTimers = new Map();

    const onSubEnter = (e) => {
      if (window.innerWidth < DESKTOP) return;
      const item = e.currentTarget;
      const pending = subLeaveTimers.get(item);
      if (pending) { clearTimeout(pending); subLeaveTimers.delete(item); }
      for (const sibling of item.parentElement?.children ?? []) {
        if (sibling !== item && sibling.classList.contains('active')) startSubNavSwitch();
        if (sibling !== item) sibling.classList.remove('active');
      }
      item.classList.add('active');
    };

    const onSubLeave = (e) => {
      if (window.innerWidth < DESKTOP) return;
      const item = e.currentTarget;
      if (e.relatedTarget && item.contains(e.relatedTarget)) return;
      subLeaveTimers.set(item, setTimeout(() => {
        subLeaveTimers.delete(item);
        item.classList.remove('active');
      }, 300));
    };

    const onBgDismiss = () => {
      if (window.innerWidth < DESKTOP) return;
      deactivate();
    };

    for (const item of navItems) item.addEventListener('mouseenter', onNavEnter);
    for (const item of subItems) {
      item.addEventListener('mouseenter', onSubEnter);
      item.addEventListener('mouseleave', onSubLeave);
    }
    if (bg) {
      bg.addEventListener('mouseenter', onBgDismiss);
      bg.addEventListener('click', onBgDismiss);
    }

    this.#destroyDesktopNav = () => {
      if (navSwitchTimer) clearTimeout(navSwitchTimer);
      if (subNavSwitchTimer) clearTimeout(subNavSwitchTimer);
      delete header.dataset.ucNavSwitching;
      delete header.dataset.ucSubNavSwitching;
      deactivate();
      for (const item of navItems) item.removeEventListener('mouseenter', onNavEnter);
      for (const item of subItems) {
        item.removeEventListener('mouseenter', onSubEnter);
        item.removeEventListener('mouseleave', onSubLeave);
      }
      if (bg) {
        bg.removeEventListener('mouseenter', onBgDismiss);
        bg.removeEventListener('click', onBgDismiss);
      }
    };
  }

  /**
   * Sets up mobile-only (<1024px) nav click behaviour.
   * Tapping a nav link that has a sidebar sibling toggles `header__sidebar--active`
   * on the sidebar instead of navigating.
   */
  #initMobileNav() {
    const DESKTOP = 1024;
    const navWrapper = /** @type {HTMLElement | null} */ (this.querySelector('.nav__wrapper'));
    const navToggle = this.querySelector('.nav__toggle');
    const navClose = this.querySelector('.nav__close');
    const navLinks = this.querySelectorAll('.header-nav__link[data-uc-sidebar-trigger]');
    const closeBtns = this.querySelectorAll('.header__sidebar-step-1-close');

    const onToggle = () => {
      if (window.innerWidth >= DESKTOP) return;
      navWrapper?.classList.add('nav__active');
      document.body.classList.add('lock-scroll');
    };

    const onNavClose = () => {
      if (window.innerWidth >= DESKTOP) return;
      if (navWrapper) {
        for (const sidebar of navWrapper.querySelectorAll('.header__sidebar--active')) {
          sidebar.classList.remove('header__sidebar--active');
        }
        for (const sublist of navWrapper.querySelectorAll('.sidebar__sublist--active')) {
          sublist.classList.remove('sidebar__sublist--active');
        }
      }
      navWrapper?.classList.remove('nav__active');
      document.body.classList.remove('lock-scroll');
    };

    const onLinkClick = (e) => {
      if (window.innerWidth >= DESKTOP) return;

      const link = e.currentTarget;
      const sidebar = link.nextElementSibling;
      if (!sidebar || !sidebar.classList.contains('header__sidebar')) return;

      e.preventDefault();
      sidebar.classList.add('header__sidebar--active');
    };

    const onSidebarClose = (e) => {
      if (window.innerWidth >= DESKTOP) return;

      const sidebar = e.currentTarget.closest('.header__sidebar');
      if (sidebar) sidebar.classList.remove('header__sidebar--active');
    };

    const sidebarLinks = this.querySelectorAll('.sidebar__item--has-children > .sidebar__link');
    const sublistCloseBtns = this.querySelectorAll('.sidebar__sublist-close');

    const onSidebarLinkClick = (e) => {
      if (window.innerWidth >= DESKTOP) return;

      const link = e.currentTarget;
      const sublist = link.nextElementSibling;
      if (!sublist || !sublist.classList.contains('sidebar__sublist')) return;

      e.preventDefault();
      sublist.classList.add('sidebar__sublist--active');
    };

    const onSublistClose = (e) => {
      if (window.innerWidth >= DESKTOP) return;

      const sublist = e.currentTarget.closest('.sidebar__sublist');
      if (sublist) sublist.classList.remove('sidebar__sublist--active');
    };

    const onClickOutside = (e) => {
      if (window.innerWidth >= DESKTOP) return;
      if (!navWrapper?.classList.contains('nav__active')) return;
      if (navWrapper.contains(e.target) || navToggle?.contains(e.target)) return;
      onNavClose();
    };

    const DISMISS_THRESHOLD = 0.25;
    const VELOCITY_THRESHOLD = 0.5;
    let touchStartY = 0;
    let touchCurrentY = 0;
    let isDragging = false;
    let touchStartTime = 0;

    const getScrollableParent = (el) => {
      let node = el;
      while (node && node !== navWrapper) {
        if (node.scrollHeight > node.clientHeight && node.scrollTop > 0) return node;
        node = node.parentElement;
      }
      return null;
    };

    const onTouchStart = (e) => {
      if (window.innerWidth >= DESKTOP) return;
      if (!navWrapper?.classList.contains('nav__active')) return;
      if (getScrollableParent(e.target)) return;

      touchStartY = e.touches[0].clientY;
      touchCurrentY = touchStartY;
      touchStartTime = Date.now();
      isDragging = false;
    };

    const onTouchMove = (e) => {
      if (window.innerWidth >= DESKTOP || !navWrapper) return;
      if (!navWrapper.classList.contains('nav__active') && !isDragging) return;

      const clientY = e.touches[0].clientY;
      const deltaY = clientY - touchStartY;

      if (!isDragging) {
        if (deltaY <= 0) return;
        isDragging = true;
        navWrapper.style.transition = 'none';
      }

      touchCurrentY = clientY;
      const clampedDelta = Math.max(0, deltaY);
      navWrapper.style.transform = `translateY(${clampedDelta}px)`;
    };

    const onTouchEnd = () => {
      if (!isDragging || !navWrapper) return;
      isDragging = false;

      const deltaY = touchCurrentY - touchStartY;
      const elapsed = Date.now() - touchStartTime;
      const velocity = deltaY / elapsed;
      const wrapperHeight = navWrapper.offsetHeight;
      const shouldDismiss = deltaY > wrapperHeight * DISMISS_THRESHOLD || velocity > VELOCITY_THRESHOLD;

      navWrapper.style.transition = '';
      navWrapper.style.transform = '';

      if (shouldDismiss) {
        onNavClose();
      }
    };

    navToggle?.addEventListener('click', onToggle);
    navClose?.addEventListener('click', onNavClose);
    document.addEventListener('click', onClickOutside);
    navWrapper?.addEventListener('touchstart', onTouchStart, { passive: true });
    navWrapper?.addEventListener('touchmove', onTouchMove, { passive: false });
    navWrapper?.addEventListener('touchend', onTouchEnd);
    navWrapper?.addEventListener('touchcancel', onTouchEnd);
    for (const link of navLinks) link.addEventListener('click', onLinkClick);
    for (const btn of closeBtns) btn.addEventListener('click', onSidebarClose);
    for (const link of sidebarLinks) link.addEventListener('click', onSidebarLinkClick);
    for (const btn of sublistCloseBtns) btn.addEventListener('click', onSublistClose);

    this.#destroyMobileNav = () => {
      navToggle?.removeEventListener('click', onToggle);
      navClose?.removeEventListener('click', onNavClose);
      document.removeEventListener('click', onClickOutside);
      navWrapper?.removeEventListener('touchstart', onTouchStart);
      navWrapper?.removeEventListener('touchmove', onTouchMove);
      navWrapper?.removeEventListener('touchend', onTouchEnd);
      navWrapper?.removeEventListener('touchcancel', onTouchEnd);
      for (const link of navLinks) link.removeEventListener('click', onLinkClick);
      for (const btn of closeBtns) btn.removeEventListener('click', onSidebarClose);
      for (const link of sidebarLinks) link.removeEventListener('click', onSidebarLinkClick);
      for (const btn of sublistCloseBtns) btn.removeEventListener('click', onSublistClose);
    };
  }

  /**
   * Wires the account sidebar (shared `.sidebar-wrapper` rendered by
   * `account-sidebar.liquid`). Clicking the toggle opens/closes it on all
   * viewports; this is the single owner of the open state (the trigger has no
   * data-uc-sidebar-target, so sidebars.js no longer opens it).
   */
  #initAccountSidebar() {
    const wrapper = document.querySelector('#account-sidebar.sidebar-wrapper');
    if (!wrapper) return;

    const toggles = this.querySelectorAll('.account__sidebar-toggle');

    const open = () => {
      wrapper.classList.add('sidebar-wrapper--active');
      document.body.classList.add('sidebar-open', 'lock-scroll');
      document.documentElement.classList.add('body-sidebar-open');
    };

    const close = () => {
      wrapper.classList.remove('sidebar-wrapper--active');
      document.body.classList.remove('sidebar-open', 'lock-scroll');
      document.documentElement.classList.remove('body-sidebar-open');
    };

    const onToggleClick = (e) => {
      e.preventDefault();
      if (wrapper.classList.contains('sidebar-wrapper--active')) {
        close();
      } else {
        open();
      }
    };

    for (const toggle of toggles) {
      toggle.addEventListener('click', onToggleClick);
    }

    this.#destroyAccountSidebar = () => {
      for (const toggle of toggles) {
        toggle.removeEventListener('click', onToggleClick);
      }
      close();
    };
  }

  /* ----------------------------------------------------------------
   *  Lifecycle
   * -------------------------------------------------------------- */

  connectedCallback() {
    super.connectedCallback();
    this.#measureScrollCollapseDistance();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.#initDesktopNav();
    this.#initMobileNav();
    this.#initAccountSidebar();

    document.addEventListener('scroll', this.#handleWindowScroll);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode === 'always') {
      this.#observeStickyPosition(true);
    }

    this.#handleWindowScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.#destroyDesktopNav?.();
    this.#destroyMobileNav?.();
    this.#destroyAccountSidebar?.();
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    this.#clearStickyAppearanceTimeout();
    this.classList.remove('header--sticky', 'header--scroll-up-visible', 'header--has-scrolled');
    this.#isStickyClassActive = false;
    this.style.removeProperty('--header-top-offset');
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
